import React from 'react';
import { Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, Line, LineChart, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Panel } from './AdminPrimitives';
import { chartAxis, chartPalette, chartTooltip } from '../../utils/chartTokens';

/** Brand series palette, shared with the mobile charts. */
export const seriesColors = chartPalette;
const axis = chartAxis;
const tooltipStyle = chartTooltip;
export interface Series {
  id: string;
  name: string;
}
interface ChartProps {
  /** The question this chart answers. Charts without one do not belong here. */
  title: string;
  question: string;
  data: Record<string, string | number>[];
  xKey: string;
  series: Series[];
  height?: number;
  action?: React.ReactNode;
}
export function AdminLineChart({
  title,
  question,
  data,
  xKey,
  series,
  height = 260,
  action
}: ChartProps) {
  return <Panel title={title} hint={question} action={action} data-id="element-211" data-mp="h03cgmra3ep0">
      <div style={{
      height
    }} data-id="element-212" data-mp="1sn7khh5290bu">
        <ResponsiveContainer width="100%" height="100%" data-id="element-213" data-mp="1smfo443n2yki">
          <LineChart data={data} margin={{
          top: 4,
          right: 8,
          left: -18,
          bottom: 0
        }} data-id="element-214" data-mp="1noe33dmnkp6e">
            <CartesianGrid stroke={axis.grid} vertical={false} data-id="element-215" data-mp="1nqqknpoi0xix" />
            <XAxis dataKey={xKey} tick={axis.tick} stroke={axis.stroke} data-id="element-216" data-mp="1iwctjzgvdck2" />
            <YAxis tick={axis.tick} stroke={axis.stroke} data-id="element-217" data-mp="1dzcqlxfbgpx6" />
            <Tooltip {...tooltipStyle} data-id="element-218" data-mp="1dwqi9vklq6dw" />
            {series.map((item, index) => <Line key={item.id} type="monotone" dataKey={item.id} name={item.name} stroke={seriesColors[index % seriesColors.length]} strokeWidth={2} dot={false} data-id="element-219" data-mp="18xdlai00nixl" data-mpi={index} />)}
          </LineChart>
        </ResponsiveContainer>
      </div>
      <Legend series={series} data-id="element-220" data-mp="2ubrpczxcdydl" />
    </Panel>;
}
export function AdminAreaChart({
  title,
  question,
  data,
  xKey,
  series,
  height = 260
}: ChartProps) {
  return <Panel title={title} hint={question} data-id="element-221" data-mp="no0wohr1sqsl">
      <div style={{
      height
    }} data-id="element-222" data-mp="nmzmefhuw8bx">
        <ResponsiveContainer width="100%" height="100%" data-id="element-223" data-mp="sfaj79have09">
          <AreaChart data={data} margin={{
          top: 4,
          right: 8,
          left: -18,
          bottom: 0
        }} data-id="element-224" data-mp="sg2d8fv913of">
            <CartesianGrid stroke={axis.grid} vertical={false} data-id="element-225" data-mp="drfpbprk33ll" />
            <XAxis dataKey={xKey} tick={axis.tick} stroke={axis.stroke} data-id="element-226" data-mp="ip7sjfxnic53" />
            <YAxis tick={axis.tick} stroke={axis.stroke} data-id="element-227" data-mp="ind3bpfcjxv7" />
            <Tooltip {...tooltipStyle} data-id="element-228" data-mp="16g3dbt3ow72u" />
            {series.map((item, index) => <Area key={item.id} type="monotone" dataKey={item.id} name={item.name} stroke={seriesColors[index % seriesColors.length]} fill={seriesColors[index % seriesColors.length]} fillOpacity={0.14} strokeWidth={2} data-id="element-229" data-mp="1bees8i8ud7rx" data-mpi={index} />)}
          </AreaChart>
        </ResponsiveContainer>
      </div>
      <Legend series={series} data-id="element-230" data-mp="3m7hm86h7y2il" />
    </Panel>;
}
export function AdminBarChart({
  title,
  question,
  data,
  xKey,
  series,
  height = 260,
  horizontal = false
}: ChartProps & {
  horizontal?: boolean;
}) {
  return <Panel title={title} hint={question} data-id="element-231" data-mp="1zbumfaadad1p">
      <div style={{
      height
    }} data-id="element-232" data-mp="2d880e9fozwbf">
        <ResponsiveContainer width="100%" height="100%" data-id="element-233" data-mp="2d8gdo2fq93n5">
          <BarChart data={data} layout={horizontal ? 'vertical' : 'horizontal'} margin={{
          top: 4,
          right: 12,
          left: horizontal ? 24 : -18,
          bottom: 0
        }} data-id="element-234" data-mp="297eetmpq2tlp">
            <CartesianGrid stroke={axis.grid} vertical={horizontal} horizontal={!horizontal} data-id="element-235" data-mp="1gjrzjh74u0j0" />
            {horizontal ? <>
                <XAxis type="number" tick={axis.tick} stroke={axis.stroke} data-id="element-236" data-mp="1uchxmjzxqvwh" />
                <YAxis type="category" dataKey={xKey} tick={axis.tick} stroke={axis.stroke} width={96} data-id="element-237" data-mp="1pivlcsxoou17" />
              </> : <>
                <XAxis dataKey={xKey} tick={axis.tick} stroke={axis.stroke} data-id="element-238" data-mp="1ai8h0skkg09j" />
                <YAxis tick={axis.tick} stroke={axis.stroke} data-id="element-239" data-mp="1fgt73z63t3k9" />
              </>}
            <Tooltip {...tooltipStyle} data-id="element-240" data-mp="1fegpxo866x89" />
            {series.map((item, index) => <Bar key={item.id} dataKey={item.id} name={item.name} fill={seriesColors[index % seriesColors.length]} radius={horizontal ? [0, 6, 6, 0] : [6, 6, 0, 0]} maxBarSize={horizontal ? 18 : 34} data-id="element-241" data-mp="1kdtc06r7w09s" data-mpi={index} />)}
          </BarChart>
        </ResponsiveContainer>
      </div>
      <Legend series={series} data-id="element-242" data-mp="237713v891chh" />
    </Panel>;
}
export function AdminDonutChart({
  title,
  question,
  data,
  nameKey,
  valueKey,
  height = 240
}: {
  title: string;
  question: string;
  data: Record<string, string | number>[];
  nameKey: string;
  valueKey: string;
  height?: number;
}) {
  return <Panel title={title} hint={question} data-id="element-243" data-mp="drpq5gausbce">
      <div style={{
      height
    }} data-id="element-244" data-mp="68gh5o69eenv">
        <ResponsiveContainer width="100%" height="100%" data-id="element-245" data-mp="68q9vjiouzx9">
          <PieChart data-id="element-246" data-mp="1hhzol9jbaj1">
            <Tooltip {...tooltipStyle} data-id="element-247" data-mp="1gydutgrk1zq" />
            <Pie data={data} dataKey={valueKey} nameKey={nameKey} innerRadius="58%" outerRadius="84%" paddingAngle={2} stroke="none" data-id="element-248" data-mp="1fx4ctfebnk4">
              {data.map((_, index) => <Cell key={index} fill={seriesColors[index % seriesColors.length]} data-id="element-249" data-mp="p6aolxrqoi45" data-mpi={index} />)}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
      </div>
      <ul className="mt-1 flex flex-wrap gap-x-4 gap-y-1" data-id="element-250" data-mp="f7lg2vlqga0k">
        {data.map((item, index) => <li key={String(item[nameKey])} className="flex items-center gap-[6px] text-[11.5px] text-muted" data-id="element-251" data-mp="a8ia98xxhzrj" data-mpi={index}>
            <span className="h-[9px] w-[9px] rounded-[3px]" style={{
          background: seriesColors[index % seriesColors.length]
        }} aria-hidden="true" data-id="element-252" data-mp="2zwhhxpwvwa2o" />
            {String(item[nameKey])}
            <span className="bee-num font-bold text-deep" data-id="element-253" data-mp="1r2f7uo60rben">{String(item[valueKey])}%</span>
          </li>)}
      </ul>
    </Panel>;
}
function Legend({
  series
}: {
  series: Series[];
}) {
  if (series.length < 2) return null;
  return <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-1" data-id="element-254" data-mp="1hze4uwcga0f7">
      {series.map((item, index) => <li key={item.id} className="flex items-center gap-[6px] text-[11.5px] text-muted" data-id="element-255" data-mp="13arnyq76d0lv" data-mpi={index}>
          <span className="h-[9px] w-[9px] rounded-[3px]" style={{
        background: seriesColors[index % seriesColors.length]
      }} aria-hidden="true" data-id="element-256" data-mp="184m7ntms5fky" />
          {item.name}
        </li>)}
    </ul>;
}

/** Horizontal distribution rows — lighter than a chart for ranked lists. */
export function DistributionList({
  items,
  unit = '%'
}: {
  items: {
    label: string;
    value: number;
    secondary?: string;
  }[];
  unit?: string;
}) {
  const max = Math.max(...items.map(item => item.value));
  return <ul className="space-y-2" data-id="element-257" data-mp="1p6cpjio5hqd">
      {items.map((item, index) => <li key={item.label} data-id="element-258" data-mp="1qzlp5gr8e1j" data-mpi={index}>
          <div className="flex items-baseline justify-between gap-2" data-id="element-259" data-mp="foo3u5jft72w">
            <span className="text-[12.5px] font-medium text-deep" data-id="element-260" data-mp="fmuugihfw8r7">{item.label}</span>
            <span className="bee-num text-[12px] text-muted" data-id="element-261" data-mp="ao9wyqtko36g">
              {item.secondary ?? `${item.value}${unit}`}
            </span>
          </div>
          <div className="mt-1 h-[7px] w-full overflow-hidden rounded-pill bg-sand-deep" data-id="element-262" data-mp="kok7u4s4wyig">
            <span className="block h-full rounded-pill" style={{
          width: `${item.value / max * 100}%`,
          background: seriesColors[index % seriesColors.length]
        }} data-id="element-263" data-mp="yf5s9xx8lgig" />
          </div>
        </li>)}
    </ul>;
}